import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import HeroSliders from "./HeroSliders";
import HeroLogoCloud from "./HeroLogoCloud";

const SPECIALTIES = [
    "Shopify Development",
    "Shopify Store Design",
    "Speed Optimisation",
    "Custom Apps & Integrations",
];

const LINES = [
    <>We build digital</>,
    <>
        products that <span className="text-brand">move</span>
    </>,
    <>
        businesses forward<span className="text-brand">.</span>
    </>,
];

export default function Hero() {
    const [specialty, setSpecialty] = useState(0);

    useEffect(() => {
        const t = setInterval(() => setSpecialty((i) => (i + 1) % SPECIALTIES.length), 2800);
        return () => clearInterval(t);
    }, []);

    return (
        <section
            data-testid="hero-section"
            className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden bg-gradient-to-b from-blue-50 via-paper to-paper"
        >
            <div className="hero-grid absolute inset-0" aria-hidden="true" />
            <div
                className="pointer-events-none absolute -top-32 right-[-10%] h-[520px] w-[560px] rounded-full bg-brand/[0.12] blur-[130px]"
                aria-hidden="true"
            />
            <div
                className="pointer-events-none absolute bottom-[-15%] left-[-8%] h-[420px] w-[420px] rounded-full bg-brand/[0.08] blur-[130px]"
                aria-hidden="true"
            />

            <div className="relative z-10 mx-auto w-full max-w-7xl flex-1 px-4 pt-32 pb-16 sm:px-8 lg:px-16">
                <div className="grid h-full grid-cols-1 items-center gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    data-testid="hero-eyebrow"
                    className="mb-8 flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-brand sm:text-xs"
                >
                    <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping-soft rounded-full bg-brand" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-brand" />
                    </span>
                    Apan Code Labs LLP — Software Studio
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    data-testid="hero-specialty-slider"
                    className="mb-8 flex flex-wrap items-center gap-x-3 gap-y-2"
                    aria-live="polite"
                >
                    <span className="rounded-full border border-brand/25 bg-white/80 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 backdrop-blur-sm">
                        Specialists in
                    </span>
                    <span className="relative inline-flex h-9 items-center overflow-hidden rounded-full bg-brand px-4">
                        <AnimatePresence mode="wait">
                            <motion.span
                                key={specialty}
                                initial={{ y: 22, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: -22, opacity: 0 }}
                                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                className="whitespace-nowrap font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-white"
                            >
                                {SPECIALTIES[specialty]}
                            </motion.span>
                        </AnimatePresence>
                    </span>
                    <span className="flex items-center gap-1.5" aria-hidden="true">
                        {SPECIALTIES.map((_, i) => (
                            <span
                                key={i}
                                className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${
                                    i === specialty ? "w-5 bg-brand" : "w-1.5 bg-slate-300"
                                }`}
                            />
                        ))}
                    </span>
                </motion.div>

                <h1
                    data-testid="hero-headline"
                    className="max-w-5xl font-display text-[2.6rem] font-bold leading-[1.05] tracking-tight text-balance text-slate-900 sm:text-6xl lg:text-[3.6rem] xl:text-[4.6rem]"
                >
                    {LINES.map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                initial={{ y: "115%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 1, delay: 0.25 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.75 }}
                    data-testid="hero-subtext"
                    className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg"
                >
                    Apan Code Labs is a software development company building modern websites,
                    eCommerce experiences, web applications and custom digital solutions for
                    businesses worldwide.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.9 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <Link
                        to="/contact"
                        data-testid="hero-start-project-btn"
                        className="group flex items-center gap-2.5 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-white shadow-card transition-[background-color,transform,box-shadow] duration-300 hover:bg-brand-dark hover:shadow-hover active:scale-95"
                    >
                        Start a Project
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                    <Link
                        to="/services"
                        data-testid="hero-explore-services-btn"
                        className="group flex items-center gap-2.5 rounded-full border border-brand/30 bg-white px-7 py-4 text-sm font-semibold text-brand transition-[border-color,box-shadow,transform,background-color] duration-300 hover:border-brand hover:bg-brand-subtle active:scale-95"
                    >
                        Explore Services
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </motion.div>

                <HeroSliders />
                    </div>
                    <HeroLogoCloud />
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.3 }}
                className="relative z-10 mt-6"
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between border-t border-brand/15 px-4 py-6 sm:px-8 lg:px-16">
                    <p data-testid="hero-credibility-line" className="font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-slate-500">
                        Based in India · Working Worldwide
                    </p>
                    <div className="hidden items-center gap-3 sm:flex" aria-hidden="true">
                        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Scroll</span>
                        <span className="relative h-8 w-px overflow-hidden bg-brand/20">
                            <motion.span
                                className="absolute left-0 top-0 h-3 w-px bg-brand"
                                animate={{ y: [-12, 32] }}
                                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                            />
                        </span>
                    </div>
                </div>
            </motion.div>
        </section>
    );
}
